import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  withTextLocalizer,
  LocalizePropType,
  setIsVerifyingGDPREmail,
  setIsEditingPhone,
  getUser,
  isKYCApp,
} from 'getaway-data-layer';
import {
  attachInputListeners,
  isDashboardScreen,
  navigateTo,
  removeInputListeners,
  showAlert,
} from '../../../utils';
import ConfirmEmailView from './ConfirmEmailView';
import { useQuery } from '../../../hooks/useQuery';
import {
  loginWithEmail,
  setCustomBasicAuth,
} from 'getaway-data-layer/src/redux/store/userData/user/actions';

type Props = {
  localize: LocalizePropType;
};

const ConfirmEmailContainer = ({ localize }: Props) => {
  const history = useHistory();
  const query = useQuery();
  const dispatch = useDispatch();
  const email = query.get('email');

  const [code, setCode] = useState('');

  const turnstileRef = useRef<HTMLDivElement>(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(
    null
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const cleanupTurnstile = () => {
    if (turnstileWidgetId && window.turnstile) {
      try {
        // @ts-ignore
        window.turnstile.remove(turnstileWidgetId);
      } catch (e) {
        console.error('Error removing turnstile widget:', e);
      }
    }
    setTurnstileWidgetId(null);
    setTurnstileToken(null);
  };

  const turnstileCheck = () => {
    // Clean up any existing widget before creating a new one
    cleanupTurnstile();

    // Create new widget
    const widgetId = window.turnstile.render(turnstileRef.current, {
      sitekey: process.env.VITE_TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setTurnstileToken(token);
      },
      theme: 'light',
      retry: 'never',
    });

    setTurnstileWidgetId(widgetId);
  };

  const login = () => {
    dispatch(
      loginWithEmail(
        {
          email,
          verificationCode: code,
          captchaToken: turnstileToken,
        },
        (data) => {
          console.log('data', data);
          if (data?.authenticationToken) {
            dispatch(
              setCustomBasicAuth(
                `Bearer ${btoa(data?.authenticationToken)}`,
                () => {
                  dispatch(
                    getUser(() => {
                      navigateTo(history, isKYCApp() ? '/settings' : '/map');
                    })
                  );
                  cleanupTurnstile();
                }
              )
            );
          }
        },
        () => {
          cleanupTurnstile();
        }
      )
    );
  };

  const onSubmit = (code: string) => {
    turnstileCheck();
  };

  const onClose = () => {
    dispatch(setIsVerifyingGDPREmail(false));
    dispatch(setIsEditingPhone(false));
    if (isDashboardScreen()) {
      navigateTo(history, '/settings');
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  useEffect(() => {
    let countdownUpdaterId: any;
    const code = query.get('verificationCode');
    if (code) {
      onSubmit(code);
    }
    attachInputListeners();

    setTimeout(() => {
      const inputElement = document?.querySelector(
        'input[name="one-time-code"]'
      );
      // @ts-ignore
      inputElement && inputElement?.click();
    }, 300);

    return () => {
      if (countdownUpdaterId) {
        clearInterval(countdownUpdaterId);
      }
      removeInputListeners();
    };
  }, []);

  useEffect(() => {
    if (turnstileToken) {
      login();
    }
  }, [turnstileToken]);

  return (
    <ConfirmEmailView
      localize={localize}
      email={email}
      onClose={onClose}
      onSubmit={onSubmit}
      code={code}
      setCode={setCode}
      turnstileRef={turnstileRef}
    />
  );
};

export default withTextLocalizer(ConfirmEmailContainer);
